import { useState } from 'react';
import { Input, Textarea, Button, Badge, Tag, Icon } from '@lib/ui';
import { PersonaFieldHelp } from '../../components';
import type { WizardPersona } from '../../types';
import { TRAIT_SUGGESTIONS, defaultVoice } from '../../types';

export type VoiceStepProps = {
  persona: Partial<WizardPersona>;
  onChange: (updates: Partial<WizardPersona>) => void;
};

export function VoiceStep({ persona, onChange }: VoiceStepProps) {
  const [newPhrase, setNewPhrase] = useState('');
  const [newAvoidPhrase, setNewAvoidPhrase] = useState('');

  const voice = persona.voice || defaultVoice;
  const traits = voice.traits || [];
  const examplePhrases = voice.examplePhrases || [];
  const avoidPhrases = voice.avoidPhrases || [];

  const updateVoice = (updates: Partial<WizardPersona['voice']>) => {
    onChange({
      voice: {
        ...voice,
        ...updates,
      },
    });
  };

  const toggleTrait = (trait: string) => {
    if (traits.includes(trait)) {
      updateVoice({ traits: traits.filter((t) => t !== trait) });
    } else if (traits.length < 5) {
      updateVoice({ traits: [...traits, trait] });
    }
  };

  const addPhrase = (type: 'example' | 'avoid') => {
    if (type === 'example' && newPhrase.trim()) {
      updateVoice({ examplePhrases: [...examplePhrases, newPhrase.trim()] });
      setNewPhrase('');
    } else if (type === 'avoid' && newAvoidPhrase.trim()) {
      updateVoice({ avoidPhrases: [...avoidPhrases, newAvoidPhrase.trim()] });
      setNewAvoidPhrase('');
    }
  };

  const removePhrase = (type: 'example' | 'avoid', index: number) => {
    if (type === 'example') {
      updateVoice({ examplePhrases: examplePhrases.filter((_, i) => i !== index) });
    } else {
      updateVoice({ avoidPhrases: avoidPhrases.filter((_, i) => i !== index) });
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">Voice</h2>
        <p className="mt-1 text-sm text-gray-500 dark:text-dark-400">
          Define how this persona speaks and writes
        </p>
      </div>

      {/* Personality traits */}
      <PersonaFieldHelp
        tooltip="Select 3-5 traits that define how this persona comes across to readers"
        example="A Thought Leader might be Authoritative, Visionary, and Direct"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
            Personality Traits
            <span className="ml-2 text-gray-400 dark:text-dark-500 font-normal">
              ({traits.length}/5 selected)
            </span>
          </label>

          {/* Selected traits */}
          {traits.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {traits.map((trait) => (
                <Badge
                  key={trait}
                  variant="filled"
                  color="primary"
                  className="cursor-pointer"
                  onClick={() => toggleTrait(trait)}
                >
                  {trait}
                  <span className="ml-1 opacity-70">&times;</span>
                </Badge>
              ))}
            </div>
          )}

          {/* Available traits */}
          <div className="flex flex-wrap gap-2">
            {TRAIT_SUGGESTIONS.filter((t) => !traits.includes(t)).map((trait) => (
              <Tag
                key={trait}
                size="md"
                variant="outlined"
                color="neutral"
                className="cursor-pointer hover:border-primary-500 hover:text-primary-600 dark:hover:border-primary-400 dark:hover:text-primary-400 transition-colors"
                onClick={() => toggleTrait(trait)}
              >
                {trait}
              </Tag>
            ))}
          </div>
        </div>
      </PersonaFieldHelp>

      {/* Voice description */}
      <PersonaFieldHelp
        tooltip="Describe the overall communication style of this persona"
        example="Speaks like she's texting her best friend. Uses emojis, exclamation marks, and rhetorical questions freely."
        suggestions={[
          'Uses data to support claims',
          'Shares personal experiences',
          'Asks thought-provoking questions',
          'Uses analogies and metaphors',
        ]}
        onSuggestionClick={(suggestion) => {
          const current = voice.description || '';
          const newDesc = current ? `${current} ${suggestion}.` : `${suggestion}.`;
          updateVoice({ description: newDesc });
        }}
      >
        <Textarea
          label="Voice Description"
          placeholder="Describe how this persona communicates..."
          value={voice.description || ''}
          onChange={(e) => updateVoice({ description: e.target.value })}
          rows={3}
        />
      </PersonaFieldHelp>

      {/* Example phrases */}
      <PersonaFieldHelp
        tooltip="Add phrases that this persona would typically use"
        example="Here's the thing nobody's talking about..."
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
            Example Phrases
            <span className="ml-2 text-gray-400 dark:text-dark-500 font-normal">
              "Sounds like this"
            </span>
          </label>

          {/* Add new phrase */}
          <div className="flex gap-2 mb-3">
            <Input
              value={newPhrase}
              onChange={(e) => setNewPhrase(e.target.value)}
              placeholder="Add a phrase this persona would use..."
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && addPhrase('example')}
            />
            <Button
              variant="outlined"
              onClick={() => addPhrase('example')}
              disabled={!newPhrase.trim()}
            >
              <Icon name="plus" size="sm" />
              Add
            </Button>
          </div>

          {/* Phrase list */}
          {examplePhrases.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {examplePhrases.map((phrase, idx) => (
                <Badge
                  key={idx}
                  variant="soft"
                  color="success"
                  className="cursor-pointer"
                  onClick={() => removePhrase('example', idx)}
                >
                  "{phrase}"<span className="ml-1 opacity-70">&times;</span>
                </Badge>
              ))}
            </div>
          )}

          {examplePhrases.length === 0 && (
            <p className="text-sm text-gray-400 dark:text-dark-500 italic">
              No example phrases added yet
            </p>
          )}
        </div>
      </PersonaFieldHelp>

      {/* Phrases to avoid */}
      <PersonaFieldHelp
        tooltip="Add phrases or words this persona would never use"
        example="Just my two cents, At the end of the day"
      >
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-dark-200 mb-2">
            Phrases to Avoid
            <span className="ml-2 text-gray-400 dark:text-dark-500 font-normal">
              "Never says this"
            </span>
          </label>

          {/* Add new avoid phrase */}
          <div className="flex gap-2 mb-3">
            <Input
              value={newAvoidPhrase}
              onChange={(e) => setNewAvoidPhrase(e.target.value)}
              placeholder="Add a phrase this persona would avoid..."
              className="flex-1"
              onKeyDown={(e) => e.key === 'Enter' && addPhrase('avoid')}
            />
            <Button
              variant="outlined"
              onClick={() => addPhrase('avoid')}
              disabled={!newAvoidPhrase.trim()}
            >
              <Icon name="plus" size="sm" />
              Add
            </Button>
          </div>

          {/* Avoid phrase list */}
          {avoidPhrases.length > 0 && (
            <div className="flex flex-wrap gap-2">
              {avoidPhrases.map((phrase, idx) => (
                <Badge
                  key={idx}
                  variant="soft"
                  color="error"
                  className="cursor-pointer"
                  onClick={() => removePhrase('avoid', idx)}
                >
                  "{phrase}"<span className="ml-1 opacity-70">&times;</span>
                </Badge>
              ))}
            </div>
          )}

          {avoidPhrases.length === 0 && (
            <p className="text-sm text-gray-400 dark:text-dark-500 italic">
              No phrases to avoid added yet
            </p>
          )}
        </div>
      </PersonaFieldHelp>
    </div>
  );
}
